import { OrderDetails } from '../../../models'
import { axiosInstance } from '../../../api'
import { ApiError } from '../api-error-data'
import {
  CreateOrderRequest,
  CreateShippingLabelRequest,
  GetOrdersHistoryParams,
  OrderListItem,
  PagedOrdersHistoryApiResponse,
  ValidateParcelRequest,
  ValidateParcelResponse,
} from './models'
import { toDateTimeOffset } from '../../../utils'
import { RatesType } from '../../../store/checkout-page/types'
import { ResponseOrderDetailType } from '../../../store/order/types'
import { AxiosResponse } from 'axios'

// Shipment Rate Request Type
export type RequestShipmentInfoType = {
  listedItemId: number
  addressLine1: string
  city: string
  region: string
  postalCode: string
  countryCode: string
  name: string
}

//Shipment Rate Response Type
export type ResponseShipmentInfoType = {
  providerShipmentId: string
  rates: RatesType[]
}

function normalizePagedOrdersHistory(data: unknown): PagedOrdersHistoryApiResponse {
  const d = data as Record<string, unknown>
  return {
    values: (d.values ?? d.Values ?? []) as unknown[],
    totalPages: Number(d.totalPages ?? d.TotalPages ?? 0),
    pageIndex: Number(d.pageIndex ?? d.PageIndex ?? 0),
    pageSize: Number(d.pageSize ?? d.PageSize ?? 0),
  }
}

type OrderListDeliveryMode =
  | 'shipping'
  | 'localPickup'
  | 'shippingAndLocal'
  | undefined

function getDeliveryModeFromListed(
  li: Record<string, unknown> | undefined,
  shipParts: string[]
): OrderListDeliveryMode {
  if (li) {
    const ship = Boolean(li.isShippingAvailable ?? li.IsShippingAvailable)
    const local = Boolean(li.isLocalPickupAvailable ?? li.IsLocalPickupAvailable)
    if (ship && local) return 'shippingAndLocal'
    if (ship) return 'shipping'
    if (local) return 'localPickup'
  } else if (shipParts.length) {
    return 'shipping'
  }
  return undefined
}

/** Maps API OrderDetailResponse (camelCase or PascalCase) to OrderListItem for the orders list UI. */
export function mapOrderDetailResponseToListItem(detail: unknown): OrderListItem {
  const d = detail as Record<string, unknown>
  const h = (d.orderHeader ?? d.OrderHeader) as Record<string, unknown> | undefined
  const li = (d.listedItem ?? d.ListedItem) as Record<string, unknown> | undefined
  if (!h) {
    return {
      orderId: 0,
      orderNumber: '',
      status: 0,
      orderCreatedUtc: '',
      totalAmount: 0,
    }
  }

  const rate = (h.shippingRateProviderData ?? h.ShippingRateProviderData) as
    | Record<string, unknown>
    | undefined
  const serviceLevel = (rate?.servicelevel ??
    rate?.serviceLevel ??
    rate?.ServiceLevel) as Record<string, unknown> | undefined
  const buyerUser = (h.buyerUser ?? h.BuyerUser) as Record<string, unknown> | undefined

  const shipParts = [
    h.shipToAddressLine1 ?? h.ShipToAddressLine1,
    h.shipToCity ?? h.ShipToCity,
    h.shipToRegion ?? h.ShipToRegion,
    h.shipToCountryCode ?? h.ShipToCountryCode,
    h.shipToPostalCode ?? h.ShipToPostalCode,
  ].filter(Boolean) as string[]

  const deliveryMode = getDeliveryModeFromListed(li, shipParts)

  const createdRaw = h.orderCreatedUtc ?? h.OrderCreatedUtc
  const orderCreatedUtc =
    createdRaw != null ? new Date(createdRaw as string | number | Date).toISOString() : ''

  return {
    orderId: Number(h.orderHeaderId ?? h.OrderHeaderId ?? 0),
    orderNumber: String(h.orderNumber ?? h.OrderNumber ?? ''),
    status: Number(h.status ?? h.Status ?? 0),
    orderCreatedUtc,
    totalAmount: Number(h.totalAmount ?? h.TotalAmount ?? 0),
    subtotalAmount: Number(h.subtotalAmount ?? h.SubtotalAmount ?? 0),
    shippingAmount: Number(h.shippingAmount ?? h.ShippingAmount ?? 0),
    itemName: li ? String(li.name ?? li.Name ?? '') : undefined,
    description: li
      ? ((li.description ?? li.Description ?? undefined) as string | undefined)
      : undefined,
    listedItemId:
      li != null
        ? (() => {
            const id = Number(li.listedItemId ?? li.ListedItemId ?? 0)
            return id > 0 ? id : undefined
          })()
        : undefined,
    defaultImageId:
      li != null
        ? (li.defaultImageId ?? li.DefaultImageId) != null
          ? Number(li.defaultImageId ?? li.DefaultImageId)
          : undefined
        : undefined,
    nonprofitName: li
      ? (() => {
          const s = String(li.nonprofitName ?? li.NonprofitName ?? '').trim()
          return s || undefined
        })()
      : undefined,
    deliveryMode,
    shippingProvider: rate ? String(rate.provider ?? rate.Provider ?? '') : undefined,
    shippingRate: serviceLevel
      ? String(serviceLevel.name ?? serviceLevel.Name ?? '')
      : undefined,
    shippingName: (h.shipToName ?? h.ShipToName) as string | undefined,
    shippingAddress: shipParts.length ? shipParts.join(', ') : undefined,
    buyerName: (() => {
      const n = String(
        h.buyerName ?? h.BuyerName ?? buyerUser?.name ?? buyerUser?.Name ?? ''
      ).trim()
      return n || undefined
    })(),
    buyerEmail: (buyerUser?.email ?? buyerUser?.Email) as string | undefined,
  }
}

export const ordersApi = {
  create(request: CreateOrderRequest): Promise<AxiosResponse<OrderDetails>> {
    const finalRequest = {
      ...request,
      ...{ clientDateTime: toDateTimeOffset(request.clientDateTime) },
    }
    return axiosInstance
      .post('order/create', { ...finalRequest })
      .then((response) => response)
  },

  async getShippmentRate(
    request: RequestShipmentInfoType
  ): Promise<AxiosResponse<ResponseShipmentInfoType>> {
    const response = await axiosInstance.post(
      '/order/available-shipping-rates',
      {
        ...request,
      }
    )
    return response
  },

  async getOrderDetail(orderId: string): Promise<ResponseOrderDetailType> {
    try {
      const response = await axiosInstance.get(`/order/${orderId}`)
      return {
        ...response.data,
      }
    } catch (error) {
      throw new ApiError(error)
    }
  },

  async createShippingLabel(
    params: CreateShippingLabelRequest
  ): Promise<AxiosResponse<string>> {
    const response = await axiosInstance.post(`/order/shippinglabel/create`, {
      ...params,
    })
    return response
  },

  async validateParcelRequest(
    params: ValidateParcelRequest
  ): Promise<AxiosResponse<ValidateParcelResponse>> {
    const response = await axiosInstance.post(`/order/validate-parcel`, {
      ...params,
    })
    return response
  },

  async getNoLabelsOrder(): Promise<number> {
    try {
      const response = await axiosInstance.get(`/order/sold/nolabel`, {})
      return response.data
    } catch (error) {
      throw new ApiError(error)
    }
  },

  async getPurchaseHistory(
    params: GetOrdersHistoryParams
  ): Promise<PagedOrdersHistoryApiResponse> {
    try {
      const response = await axiosInstance.get('order/purchase-history', {
        params: {
          sortByDate: params.sortByDate,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      })
      return normalizePagedOrdersHistory(response.data)
    } catch (error) {
      throw new ApiError(error)
    }
  },

  async getSalesHistory(
    params: GetOrdersHistoryParams
  ): Promise<PagedOrdersHistoryApiResponse> {
    try {
      const response = await axiosInstance.get('order/sales-history', {
        params: {
          sortByDate: params.sortByDate,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      })
      return normalizePagedOrdersHistory(response.data)
    } catch (error) {
      throw new ApiError(error)
    }
  },
}
